import { cn } from "@/lib/utils";
import { Activity, Zap, RefreshCw } from "lucide-react";

interface BPMSyncIndicatorProps {
  currentBPM: number;
  trackBPM: number;
  isSynced: boolean;
  isAdjusting?: boolean;
  isCrossfading?: boolean;
}

export const BPMSyncIndicator = ({ 
  currentBPM, 
  trackBPM, 
  isSynced,
  isAdjusting = false,
  isCrossfading = false
}: BPMSyncIndicatorProps) => {
  const diff = Math.abs(currentBPM - trackBPM);
  
  const getStatus = () => {
    if (isCrossfading) return { icon: RefreshCw, text: 'Transição suave...', color: 'text-blue-500' };
    if (isAdjusting) return { icon: RefreshCw, text: 'Ajustando playlist...', color: 'text-blue-500' };
    if (isSynced && diff <= 10) return { icon: Activity, text: 'Perfeitamente sincronizado', color: 'text-green-500' };
    if (diff <= 15) return { icon: Zap, text: 'Sincronização boa', color: 'text-orange-500' };
    return { icon: RefreshCw, text: 'Buscando música...', color: 'text-orange-500' };
  };

  const status = getStatus();
  const StatusIcon = status.icon;
  
  return (
    <div 
      className="flex items-center justify-between p-3 bg-gradient-to-r from-muted/30 to-muted/10 rounded-lg border border-border/50"
      role="status"
      aria-live="polite"
      aria-label={`BPM sync status: ${status.text}`}
    >
      <div className="flex items-center gap-3">
        <div className="relative" aria-hidden="true">
          <div 
            className={cn(
              "w-2 h-2 rounded-full absolute -top-1 -right-1",
              isSynced && !isAdjusting ? "bg-green-500 animate-pulse" : "bg-muted"
            )}
          />
          <StatusIcon 
            className={cn(
              "w-5 h-5",
              status.color,
              (isAdjusting || isCrossfading) && "animate-spin"
            )}
          />
        </div>
        <div className="flex flex-col">
          <span className={cn("text-xs font-medium", status.color)}>
            {status.text}
          </span>
          {currentBPM > 0 && (
            <span 
              className="text-xs text-muted-foreground"
              aria-label={`Your heart rate: ${currentBPM} BPM, Track tempo: ${trackBPM} BPM`}
            >
              Você: {currentBPM} BPM • Música: {trackBPM} BPM
            </span>
          )}
        </div>
      </div>
      
      {diff > 0 && (
        <div 
          className="flex flex-col items-end"
          aria-label={`BPM difference: ${diff}`}
        >
          <span className={cn(
            "text-xs font-bold",
            diff <= 10 ? "text-green-500" : "text-orange-500"
          )}>
            Δ {diff}
          </span>
          <span className="text-xs text-muted-foreground">BPM</span>
        </div>
      )}
    </div>
  );
};